'use client';
import type { ElementType } from 'react';
import { Link, LucideIcon } from '@skin-seoul/design-system';
import {
  formatCartCurrency,
  getCartProductPackLabel,
  getCartRewardCompareAtPrice,
  getCartRewardProductTarget,
  type CartRewardProduct,
  Currency,
} from '@skin-seoul/service';
import { extractPathname, formatReviewRating } from '@skin-seoul/utils';
import { CartDiscoveryCard } from './CartDiscoveryCard';
import { useCartCardTracking, type CartCardAttribution } from './CartAnalytics';

export interface CartRewardProductCardProps {
  offer: CartRewardProduct;
  currency: Currency;
  linkComponent?: ElementType;
  onProductClick: () => void;
  onCartMutation?: (mutation: Promise<boolean>) => void;
  attribution?: CartCardAttribution;
}

export function CartRewardProductCard({
  offer,
  currency,
  linkComponent,
  onProductClick,
  onCartMutation,
  attribution,
}: CartRewardProductCardProps) {
  const LinkComponent = linkComponent || Link;
  const { product, projection, reviewStatistics } = offer;
  const tracking = useCartCardTracking(product, attribution);
  const handleProductClick = () => {
    tracking.click();
    onProductClick();
  };
  const target = getCartRewardProductTarget(offer);
  if (!target) {
    return null;
  }
  const price = projection.priceAfterReward ?? projection.price;
  const compareAtPrice = getCartRewardCompareAtPrice(offer);
  return (
    <CartDiscoveryCard
      className="w-full min-w-0"
      onImpression={tracking.impression}
      attribution={attribution}
      impressionKey={`${product.productId}:${attribution?.tab ?? ''}`}
    >
      <CartDiscoveryCard.Media>
        <LinkComponent
          href={extractPathname(product.productLink)}
          onClick={handleProductClick}
          aria-label={product.name}
          className="flex h-[200px] items-center justify-center overflow-hidden rounded-lg bg-neutral-50"
        >
          <CartDiscoveryCard.Picture
            src={product.productImageUrls[0]}
            name={product.name}
            className="top-0.5 h-[145px] w-28"
          />
        </LinkComponent>
        <CartDiscoveryCard.Add
          target={target}
          currency={currency}
          onCartMutation={onCartMutation}
          attribution={attribution}
          analyticsParams={tracking.params}
          className="size-8 [&_svg]:w-5 [&_svg]:h-5"
        />
      </CartDiscoveryCard.Media>
      <LinkComponent
        href={extractPathname(product.productLink)}
        onClick={handleProductClick}
        className="font-sans mt-2 flex min-w-0 flex-col items-start gap-2 text-left no-underline hover:no-underline"
      >
        <CartDiscoveryCard.Name
          brand={product.brand}
          name={product.name}
          packLabel={getCartProductPackLabel(product, target?.sku)}
          layout="grid"
        />
        <span className="flex flex-col gap-2">
          <span className="text-paragraph-large font-semibold text-primary-500">
            {formatCartCurrency(currency, price)}
          </span>
          {compareAtPrice !== null && (
            <span className="text-label-medium leading-5 text-neutral-400 line-through">
              {formatCartCurrency(currency, compareAtPrice)}
            </span>
          )}
        </span>
        {reviewStatistics && reviewStatistics.averageRate > 0 && (
          <span className="flex items-center gap-2 text-label-medium leading-5 text-neutral-700">
            <span className="flex items-center gap-[3px] font-medium">
              <LucideIcon name="Star" size={16} className="fill-neutral-700" />
              {formatReviewRating(reviewStatistics.averageRate)}
            </span>
            <span className="text-neutral-400">({reviewStatistics.totalReviews})</span>
          </span>
        )}
      </LinkComponent>
    </CartDiscoveryCard>
  );
}

export function CartRewardOfferCard({
  offer,
  currency,
  linkComponent,
  onProductClick,
  onCartMutation,
  attribution,
}: CartRewardProductCardProps) {
  const LinkComponent = linkComponent || Link;
  const tracking = useCartCardTracking(offer.product, attribution);
  const handleProductClick = () => {
    tracking.click();
    onProductClick();
  };
  const target = getCartRewardProductTarget(offer);
  if (!target) {
    return null;
  }
  const { product, projection } = offer;
  const packLabel = getCartProductPackLabel(product, target?.sku);
  const price = projection.priceAfterReward ?? projection.price;
  return (
    <CartDiscoveryCard
      className="relative flex min-h-[94px] w-[276px] max-w-full items-center gap-1.5 rounded-xl bg-white px-2.5 py-2 shadow-[0_2px_7px_rgba(0,0,0,0.1)]"
      onImpression={tracking.impression}
      attribution={attribution}
      impressionKey={String(product.productId)}
    >
      <LinkComponent
        href={extractPathname(product.productLink)}
        onClick={handleProductClick}
        className="flex size-[62px] shrink-0 items-center justify-center bg-neutral-50 rounded-lg"
      >
        <CartDiscoveryCard.Picture
          src={product.productImageUrls[0]}
          name={product.name}
          className="top-0 size-[62px]"
        />
      </LinkComponent>
      <LinkComponent
        href={extractPathname(product.productLink)}
        onClick={handleProductClick}
        className="flex min-w-0 flex-1 flex-col items-start gap-1.5 text-left no-underline hover:no-underline"
      >
        <span className="flex w-full flex-col items-start">
          <span className="max-w-full truncate text-label-xsmall leading-4 text-neutral-400">{product.brand}</span>
          <span className="w-full truncate text-label-medium font-medium text-neutral-700">{product.name}</span>
          {packLabel && (
            <span className="w-full truncate text-label-small leading-4 text-neutral-700">{packLabel}</span>
          )}
        </span>
        <span className={currency === Currency.Colombia ? 'flex flex-col items-start' : 'flex items-end gap-1'}>
          <span
            className={`text-label-medium font-semibold ${price < projection.price ? 'text-primary-400' : 'text-neutral-700'}`}
          >
            {formatCartCurrency(currency, price)}
          </span>
          {price < projection.price && (
            <span className="text-label-small leading-5 text-neutral-400 line-through">
              {formatCartCurrency(currency, projection.price)}
            </span>
          )}
        </span>
      </LinkComponent>
      <CartDiscoveryCard.Add
        target={target}
        currency={currency}
        onCartMutation={onCartMutation}
        attribution={attribution}
        analyticsParams={tracking.params}
        className="static size-7 shrink-0 shadow-none [&_svg]:w-5 [&_svg]:h-5"
      />
    </CartDiscoveryCard>
  );
}
